import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, FileCheck2, MailCheck, Repeat, Clock, ShieldCheck, PiggyBank, Users, Inbox, ArrowRight, Check, PlayCircle } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/track";
import Walkthrough from "@/pages/site/Walkthrough";

const STEPS = [
  { icon: Camera, t: "Photograph the invoice", d: "Staff snap each wholesaler invoice and credit claim on a phone as it arrives. The details are read for you to check." },
  { icon: FileCheck2, t: "Match it to the statement", d: "Upload the monthly statement as a CSV or PDF. Every line is checked against what you hold: wrong amounts, duplicates, missing invoices and missing credits." },
  { icon: MailCheck, t: "Query the wholesaler", d: "Each problem becomes one email to the wholesaler, in your pharmacy's name, with a reference they can reply to." },
  { icon: Repeat, t: "Follow up until credited", d: "Reminders go every 3 days. After 3 reminders the owner is alerted. A corrected statement closes the query by itself." },
];
const BENEFITS = [
  { icon: Clock, t: "Hours saved", d: "No more ticking statements by hand or hunting through paper invoices at month end." },
  { icon: ShieldCheck, t: "Errors caught", d: "Seven checks run on every statement line, so overcharges and missing credits don't slip through." },
  { icon: PiggyBank, t: "Credits recovered", d: "Every claim is tracked until it appears on a statement, with the amount still owed always visible." },
  { icon: Users, t: "Owners kept informed", d: "A morning digest, weekly wholesaler packs and a month-end summary keep the owner in the picture." },
  { icon: Inbox, t: "Nothing lost in an inbox", d: "Every query has its own thread code, its full history and its replies in one place." },
];

const Section = ({ children, className = "", id }) => <section id={id} className={`mx-auto max-w-6xl px-5 ${className}`}>{children}</section>;
const H2 = ({ children }) => <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{children}</h2>;

const Mock = () => (
  <div className="relative rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]" aria-label="Product screen">
    <div className="mb-4 flex items-center justify-between"><p className="font-display font-bold">Open queries</p><span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">3 awaiting wholesaler</span></div>
    {[["QRY-00412", "Amount differs: £120.00 on statement, £112.50 on invoice", "£7.50"], ["QRY-00413", "Credit C501 not on statement", "£15.00"], ["QRY-00414", "Invoice A1002 listed twice", "£60.00"]].map(([c, t, v]) => (
      <div key={c} className="mb-2 flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3">
        <div className="min-w-0"><p className="font-mono text-xs text-teal-700">{c}</p><p className="truncate text-sm">{t}</p></div>
        <p className="shrink-0 font-display font-bold">{v}</p>
      </div>
    ))}
    <p className="mt-3 text-[11px] text-slate-400">Illustrative example, not real customer data.</p>
  </div>
);

const Reveal = ({ children, className = "", d = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

const HERO_POINTS = [[Camera, "Snap it daily", "Photograph invoices and credit claims as they arrive."], [ShieldCheck, "Reconcile monthly", "Statements are checked line by line against what you hold."], [MailCheck, "Chase automatically", "Wholesalers get queried and reminded until it's sorted."]];
const PROOF = [["7", "checks on every statement line"], ["3 days", "between automatic reminders"], ["2 min", "to photograph a day's invoices"], ["£99", "a month for your first branch"]];
const FIGURES = [
  { icon: PiggyBank, f: "£540", t: "a month in errors, for example", d: "120 invoices × £450 average × a 1% error rate. That's more than five times the monthly price. Try your own figures below." },
  { icon: Clock, f: "Hours", t: "back at every month end", d: "No more ticking statements by hand or hunting through paper invoices in the dispensary." },
  { icon: ShieldCheck, f: "Nothing", t: "slips through", d: "Wrong amounts, duplicates, missing invoices and missing credits are flagged and chased until credited." },
];

function Hero({ c }) {
  return (
    <Section className="grid items-center gap-12 pb-24 pt-14 md:pt-20 lg:grid-cols-[1fr_1.05fr]">
      <Reveal>
        <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-teal-800"><span className="h-1.5 w-1.5 rounded-full bg-teal-500" />For UK community pharmacies</p>
        <h1 className="font-display text-4xl font-extrabold leading-[1.03] tracking-tight sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
          {(c.hero_title || "Every invoice. Every credit. Accounted for.").split(/(?<=\.)\s+/).map((l, i, a) => <span key={i} className={`block ${i === a.length - 1 && a.length > 1 ? "text-teal-600" : ""}`}>{l}</span>)}
        </h1>
        {c.hero_sub && <p className="mt-6 max-w-xl text-base text-slate-600 md:text-lg">{c.hero_sub}</p>}
        <div className="mt-8 max-w-xl space-y-5" data-testid="home-hero-points">
          {HERO_POINTS.map(([I, t, d]) => (
            <div key={t} className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-teal-200 bg-teal-50 text-teal-700"><I className="h-5 w-5" /></span>
              <div><p className="font-display text-lg font-bold">{t}</p><p className="text-sm text-slate-600 md:text-base">{d}</p></div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/signup" onClick={() => track("signup_started")} className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-4 font-semibold text-white shadow-lg shadow-slate-900/20 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-teal-700" data-testid="home-signup-button">Start now · £99/month <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          <a href="#walkthrough" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 font-semibold transition-colors hover:border-slate-900" data-testid="home-how-link"><PlayCircle className="h-5 w-5 text-teal-600" />Watch the 20-second tour</a>
        </div>
        <p className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">{["Monthly rolling", "Cancel before renewal", "Voucher = first month free"].map((x) => <span key={x} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-teal-600" />{x}</span>)}</p>
      </Reveal>
      <Reveal d={0.15} className="relative mt-8 sm:mt-0">
        <Walkthrough />
        <Badge className="-left-6 bottom-32 hidden sm:flex" f="£82.50" t="credited in the demo" testid="hero-badge-credited" />
        <Badge className="-top-14 right-14 sm:-top-12 [animation-delay:1.5s]" f="7 checks" t="on every line" testid="hero-badge-checks" />
        <Badge className="-bottom-16 right-6 [animation-delay:3s]" f="Every 3 days" t="automatic chasing" testid="hero-badge-chasing" />
      </Reveal>
    </Section>
  );
}

const Badge = ({ f, t, className, testid }) => (
  <div className={`lux-glass lux-float pointer-events-none absolute z-10 flex flex-col rounded-2xl px-4 py-2.5 shadow-[0_20px_50px_-20px_rgba(16,185,129,0.6)] ${className}`} data-testid={testid}>
    <span className="font-display text-xl font-extrabold text-teal-600">{f}</span><span className="text-[11px] font-semibold text-slate-500">{t}</span>
  </div>
);

const Proof = () => (
  <Section className="pb-6">
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 md:grid-cols-4" data-testid="home-proof-strip">
      {PROOF.map(([f, t]) => <div key={t} className="bg-white px-6 py-6"><p className="font-display text-3xl font-extrabold text-teal-700">{f}</p><p className="mt-1 text-sm text-slate-600">{t}</p></div>)}
    </div>
  </Section>
);

const SectionHead = ({ over, title, sub }) => (
  <Reveal className="mb-10 max-w-2xl">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">{over}</p>
    <H2>{title}</H2>
    {sub && <p className="mt-3 text-slate-600">{sub}</p>}
  </Reveal>
);

export function Home() {
  const cfg = useOutletContext();
  const c = cfg?.content || {};
  return (
    <div className="overflow-x-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-[640px] bg-[radial-gradient(55%_55%_at_75%_15%,rgba(16,185,129,0.22),transparent_70%),radial-gradient(40%_40%_at_10%_0%,rgba(56,189,248,0.10),transparent_70%)]" />
      <div className="relative">
        <Hero c={c} />
        <Proof />
        <Section className="scroll-mt-28 py-20" id="benefits">
          <SectionHead over="Benefits" title="It pays for itself with the first mistake it finds." sub="Wholesaler statements are long, and small errors add up. Here's what checking every line is worth." />
          <div className="grid gap-5 md:grid-cols-3" data-testid="home-benefits">
            {FIGURES.map(({ icon: I, f, t, d }, k) => (
              <Reveal key={t} d={k * 0.08} className="group rounded-3xl border border-slate-200 bg-white p-7 transition-[box-shadow,transform] hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(13,148,136,0.5)]">
                <I className="mb-5 h-6 w-6 text-teal-600" />
                <p className="font-display text-4xl font-extrabold">{f}</p><p className="font-display text-lg font-bold text-slate-700">{t}</p>
                <p className="mt-3 text-sm text-slate-600">{d}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-5 grid gap-5 md:grid-cols-2">{BENEFITS.slice(2).map(({ icon: I, t, d }) => <Reveal key={t} className="flex gap-4 rounded-3xl border border-slate-200 bg-white/70 p-6"><I className="h-5 w-5 shrink-0 text-teal-600" /><div><p className="font-semibold">{t}</p><p className="mt-1 text-sm text-slate-600">{d}</p></div></Reveal>)}</div>
        </Section>
        <section className="lux-band scroll-mt-28 py-20" id="how-it-works" data-testid="home-how-section">
          <div className="mx-auto max-w-6xl px-5">
            <Reveal className="mb-10 max-w-2xl"><p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-300">How it works</p><h2 className="font-display text-3xl font-extrabold sm:text-4xl">From the delivery tote to the credit note, in four steps.</h2></Reveal>
            <Steps dark />
          </div>
        </section>
        <Section className="scroll-mt-28 py-20" id="pricing">
          <SectionHead over="Pricing" title="One simple price. No set-up fee." sub="See your price, estimate what you could recover, and start in minutes." />
          <Reveal><PricingCard /></Reveal>
          <Reveal className="mt-8"><SavingsCalculator /></Reveal>
        </Section>
        <Section className="scroll-mt-28 py-16" id="faqs"><SectionHead over="Questions" title="Questions answered" /><Faqs items={c.faqs} /></Section>
        <Section className="pb-28 pt-8">
          <Reveal className="lux-glass relative overflow-hidden rounded-[32px] px-8 py-14 md:px-14">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-teal-500/30 blur-3xl" />
            <p className="relative font-display text-3xl font-extrabold sm:text-4xl">Ready when your next statement lands.</p>
            <p className="relative mt-3 max-w-xl text-slate-300">Set up your pharmacy, add your branches and photograph your first invoice today.</p>
            <Link to="/signup" className="relative mt-7 inline-flex items-center gap-2 rounded-full bg-teal-400 px-7 py-4 font-semibold text-slate-900 transition-colors hover:bg-teal-300" data-testid="home-bottom-signup-button">Sign up in minutes <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </Section>
      </div>
    </div>
  );
}

const Steps = ({ dark = false }) => (
  <ol className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
    {STEPS.map(({ icon: I, t, d }, i) => (
      <motion.li key={t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.08 }} className={`rounded-3xl border p-6 ${dark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
        <div className="mb-5 flex items-center justify-between"><span className={`flex h-9 w-9 items-center justify-center rounded-full font-mono text-xs font-bold ${dark ? "bg-teal-400 text-slate-900" : "bg-teal-50 text-teal-800"}`}>0{i + 1}</span><I className={`h-5 w-5 ${dark ? "text-teal-300" : "text-teal-700"}`} /></div>
        <p className="font-display text-lg font-bold">{t}</p><p className={`mt-2 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>{d}</p>
      </motion.li>
    ))}
  </ol>
);

export const Faqs = ({ items = [] }) => (
  <div className="mt-6 divide-y divide-slate-200 rounded-3xl border border-slate-200 bg-white" data-testid="faq-list">
    {items.map((f) => (
      <details key={f.q} className="group px-6 py-5"><summary className="cursor-pointer list-none font-semibold">{f.q}</summary><p className="mt-2 text-sm text-slate-600">{f.a}</p></details>
    ))}
  </div>
);

export function PricingCard({ full = false }) {
  const [n, setN] = useState(1);
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get("voucher") || localStorage.getItem("ir_voucher") || "");
  const total = 99 + 89 * (n - 1);
  return (
    <div className="grid gap-8 rounded-[32px] border border-slate-200 bg-white p-7 md:grid-cols-2 md:p-10" data-testid="pricing-card">
      <div>
        {full && <H2>Simple monthly pricing</H2>}
        <p className={`${full ? "mt-3" : ""} text-slate-600`}>£99 a month for the first branch and £89 a month for each extra branch on the same account, plus VAT if applicable. Paid by payment card.</p>
        <ul className="mt-6 space-y-2 text-sm">
          {["Monthly rolling contract, renews on your sign-up date", "Cancel before 23:59 UK time the day before renewal to stop the next charge", "No refunds for part months", "No free trial — a voucher code gives your first month free"].map((x) => <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />{x}</li>)}
        </ul>
      </div>
      <div className="rounded-3xl bg-slate-50 p-6">
        <p className="text-sm font-semibold text-slate-500">Branches</p>
        <div className="mt-3 flex items-center gap-4"><Slider min={1} max={20} step={1} value={[n]} onValueChange={([v]) => setN(v)} data-testid="pricing-branch-slider" /><span className="w-8 text-right font-display text-xl font-bold" data-testid="pricing-branch-count">{n}</span></div>
        <p className="mt-6 font-display text-5xl font-extrabold" data-testid="pricing-monthly-total">£{total}<span className="text-base font-semibold text-slate-500"> / month + VAT</span></p>
        <p className="mt-1 text-xs text-slate-500">£99 + {n - 1} × £89</p>
        <label className="mt-6 block text-sm font-semibold text-slate-500">Voucher code</label>
        <Input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); localStorage.setItem("ir_voucher", e.target.value.toUpperCase()); }} placeholder="IR-XXXXXXXX" className="mt-1 bg-white" data-testid="pricing-voucher-input" />
        <Link to={`/signup?branches=${n}${code ? `&voucher=${code}` : ""}`} onClick={() => track("signup_started")} className="mt-5 flex justify-center rounded-full bg-slate-900 px-6 py-3.5 font-semibold text-white hover:bg-teal-700" data-testid="pricing-signup-button">Agree and start subscription</Link>
        {full && <p className="mt-3 text-xs text-slate-500">You review the full terms and key points before paying.</p>}
      </div>
    </div>
  );
}

export function SavingsCalculator() {
  const [inv, setInv] = useState(120);
  const [avg, setAvg] = useState(450);
  const [rate, setRate] = useState(1);
  const est = Math.round(inv * avg * (rate / 100));
  return (
    <div className="rounded-[32px] border border-slate-200 bg-white p-7 md:p-10" data-testid="savings-calculator">
      <H2>Savings calculator</H2>
      <p className="mt-2 text-sm text-slate-600">Enter your own figures. This is a rough estimate, not a promise of what you will recover.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[["Invoices per month", inv, setInv, "calc-invoices"], ["Average invoice value (£)", avg, setAvg, "calc-average"], ["Assumed error rate (%)", rate, setRate, "calc-rate"]].map(([l, v, s, id]) => (
          <label key={id} className="text-sm font-semibold text-slate-600">{l}<Input type="number" min="0" step="any" value={v} onChange={(e) => s(Math.max(0, Number(e.target.value)))} className="mt-1" data-testid={id} /></label>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap items-end gap-8">
        <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estimated errors a month</p><p className="font-display text-4xl font-extrabold text-teal-700" data-testid="calc-estimate">£{est.toLocaleString("en-GB")}</p></div>
        <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Price for one branch</p><p className="font-display text-4xl font-extrabold">£99</p></div>
      </div>
      <p className="mt-3 text-xs text-slate-500">Estimate = invoices × average value × error rate. Your actual results depend on your wholesalers and how quickly they credit you.</p>
    </div>
  );
}

export function HowItWorks() {
  return <Section className="py-16"><h1 className="font-display text-4xl font-extrabold sm:text-5xl">How it works</h1><p className="mt-4 max-w-2xl text-slate-600">From the delivery tote to the credit note, in four steps.</p><div className="mt-10"><Walkthrough /></div><Steps /><div className="mt-12"><Mock /></div></Section>;
}

export function Benefits() {
  return (
    <Section className="py-16">
      <h1 className="font-display text-4xl font-extrabold sm:text-5xl">What your pharmacy gains</h1>
      <div className="mt-10 grid gap-5 md:grid-cols-2">{BENEFITS.map(({ icon: I, t, d }) => <div key={t} className="rounded-3xl border border-slate-200 bg-white p-7"><I className="mb-4 h-6 w-6 text-teal-700" /><p className="font-display text-xl font-bold">{t}</p><p className="mt-2 text-sm text-slate-600">{d}</p></div>)}</div>
      <div className="mt-12"><SavingsCalculator /></div>
    </Section>
  );
}

export function Pricing() {
  const cfg = useOutletContext();
  useState(() => track("pricing_view"));
  return (
    <Section className="space-y-10 py-16">
      <h1 className="font-display text-4xl font-extrabold sm:text-5xl" data-testid="pricing-title">Pricing</h1>
      <PricingCard full />
      <SavingsCalculator />
      <div><H2>Pricing questions</H2><Faqs items={[
        { q: "Is VAT included?", a: "No. Prices are £99 and £89 a month plus VAT if applicable." },
        { q: "What happens if I add a branch mid-month?", a: "The new branch is charged pro rata for the rest of the month, then in full." },
        { q: "And if I remove one?", a: "The lower price applies from your next renewal date. No refund is made for the current month." },
        { q: "What does a voucher cover?", a: "Your whole first monthly invoice, including branches added at sign-up. Your card is still collected and first charged one month later." },
        ...(cfg?.content?.faqs || []).slice(0, 2)]} /></div>
    </Section>
  );
}

export function Security() {
  return (
    <Section className="max-w-3xl py-16">
      <h1 className="font-display text-4xl font-extrabold sm:text-5xl">Security and trust</h1>
      <div className="mt-8 space-y-5 text-slate-700">
        {[["UK data protection", "We act as your processor under a UK GDPR Data Processing Agreement, which forms part of the Terms."],
          ["No patient data", "The service is built for wholesaler business documents only. Patient information must not be uploaded."],
          ["Your data, kept separate", "Every record belongs to one customer and every request is checked against it."],
          ["Second sign-in step", "Owners and managers confirm a code sent by email when they sign in."],
          ["Backups", "Daily backups, which expire within 35 days."],
          ["Certifications", "We will list certifications here once they are held."]].map(([t, d]) => <div key={t} className="rounded-2xl border border-slate-200 bg-white p-5"><p className="font-display font-bold">{t}</p><p className="mt-1 text-sm">{d}</p></div>)}
        <p className="text-sm">Read the <Link className="font-semibold text-teal-700 underline" to="/legal/subprocessors">sub-processor list</Link>, the <Link className="font-semibold text-teal-700 underline" to="/legal/dpa">Data Processing Agreement</Link> and the <Link className="font-semibold text-teal-700 underline" to="/legal/privacy">Privacy Policy</Link>.</p>
      </div>
    </Section>
  );
}
